import abcoLogo from "assets/abco-logo.png";
import { Box, Button, FormattedCurrency, HStack, Image, Link } from "components/ui";
import ItemBadge from "components/ui/ItemBadge";
import { Typography } from "components/ui/Typography";
import React, { useState } from "react";
import { ActivityIndicator, Platform } from "react-native";
import { useGetItemQuery } from "store/abcoApi";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import ItemStatusBadge from "./ItemStatusBadge";
import { useToken } from "@gluestack-style/react";
import { useIsMobile } from "utils/hooks/useIsMobile";

type Props = {
	item: any;
	isExpanded: boolean;
	cart: any;
	updateItem: any;
	isNoReplace?: boolean;
};

const ItemListItemReplacementSection = ({ item, isExpanded, cart, updateItem, isNoReplace = false }: Props) => {
	const { data: alternativeProducts, isLoading } = useGetItemQuery(
		{
			stockcodeCollection: item.item.substituteCollection as string[],
		},
		{ skip: !isExpanded || !item.item.substituteCollection?.length },
	);

	const { close: closeSiteSearch } = useSiteSearch();
	const [replacingItems, setReplacingItems] = useState<Set<string>>(new Set());
	const isMobile = useIsMobile();
	const selectReplacement = async (replacementItem: any) => {
		console.log("Replace", replacementItem);

		if (!replacementItem?.stockcode || !item?.item?.stockcode || !updateItem) {
			console.error("Missing required data for replacement", {
				replacementStockcode: replacementItem?.stockcode,
				originalStockcode: item?.item?.stockcode,
				updateItem: !!updateItem,
			});
			return;
		}

		// Track which item is being replaced to show loading state
		setReplacingItems((prev) => new Set([...prev, replacementItem.stockcode]));

		try {
			// Step 1: Remove the original item (set quantity to 0)
			await updateItem({
				stockcode: item.item.stockcode,
				quantity: 0,
			});

			console.log(`Removed original item: ${item.item.stockcode}`);

			// Step 2: Add the replacement item with the same quantity
			await updateItem({
				stockcode: replacementItem.stockcode,
				quantity: item.quantity || 1,
				isRechargeable: item.isRechargeable || false,
			});

			console.log(`Added replacement item: ${replacementItem.stockcode} with quantity: ${item.quantity || 1}`);
		} catch (error) {
			console.error("Error replacing item:", error);
			// TODO: Add user-facing error message/toast
		} finally {
			// Remove from loading state
			setReplacingItems((prev) => {
				const newSet = new Set(prev);
				newSet.delete(replacementItem.stockcode);
				return newSet;
			});
		}
	};

	let resolvedPrimary = useToken("colors", "primarylight");
	const renderProductImage = (altItem: any) => {
		return (
			<Box w={80} h={60} padding={altItem.imageUrl ? 0 : 6} bg="$basicwhite" borderRadius={8} overflow="hidden" mr={12}>
				<Link
					href={`/${altItem.slug}`}
					pressableStyle={{ display: "flex", width: "100%", height: "100%" }}
					component={(hovered: boolean) => {
						if (!altItem.imageUrl || altItem.imageUrl === "" || altItem.imageUrl === null) {
							return (
								<Box w={"$full"} h={"$full"} maxHeight={60} overflow="hidden">
									<Image
										alt={altItem.name || "Product image"}
										sx={{
											width: "100%",
											height: "100%",
											maxHeight: 40,
										}}
										resizeMode="contain"
										borderRadius={8}
										opacity={0.3}
										// @ts-ignore
										style={{ filter: "grayscale(100%)" }}
										source={abcoLogo}
									/>
								</Box>
							);
						}
						return (
							<Box w={"$full"} h={"$full"} maxHeight={60} overflow="hidden">
								<Image
									sx={{
										width: "100%",
										height: "100%",
									}}
									resizeMode="contain"
									style={{
										transform: hovered ? "scale(1.05)" : "scale(1)",
										// @ts-ignore
										transition: "transform 0.3s ease",
										opacity: hovered ? 0.7 : 1,
									}}
									borderRadius={8}
									// @ts-ignore
									source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + altItem.imageUrl + "?width=200&quality=75&format=webp"}
									alt={altItem.name || "Product image"}
								/>
							</Box>
						);
					}}
				/>
			</Box>
		);
	};

	// Web-specific animated wrapper
	if (Platform.OS === "web") {
		return (
			<div
				style={{
					// marginTop: 12,
					overflowY: "scroll",
					maxHeight: isExpanded ? "600px" : "0px",
					transition: "max-height 0.3s ease-in-out",
				}}
			>
				<div
					style={{
						opacity: isExpanded ? 1 : 0,
						transition: "opacity 0.3s ease-in-out",
					}}
				>
					<Box bg="$neutral50" borderRadius={8} p={12}>
						<HStack alignItems="center" gap={8} mb={12}>
							{/* <Icon as={ChevronDown} color="$primarylight" size="sm" /> */}
							<Typography weight="bold" color="$neutral800">
								Alternative Products
							</Typography>
						</HStack>

						{isLoading ? (
							<Box p={16} bg="$basicwhite" borderRadius={6} borderWidth={1} borderColor="$neutral300">
								<ActivityIndicator size="large" color={resolvedPrimary} />
								{/* <Typography.ParagraphSmall color="$neutral600">Loading alternative products...</Typography.ParagraphSmall> */}
							</Box>
						) : alternativeProducts?.list && alternativeProducts.list.length > 0 ? (
							<Box bg="$basicwhite" borderRadius={6} borderWidth={1} borderColor="$neutral300">
								{alternativeProducts.list?.map((altItem: any, index: number) => (
									<Box key={altItem.stockcode} p={12} borderBottomWidth={index < (alternativeProducts.list?.length || 0) - 1 ? 1 : 0} borderBottomColor="$neutral200">
										<HStack flexDirection={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} gap={isMobile ? 12 : 0}>
											{renderProductImage(altItem)}
											<Box flex={1}>
												{altItem.stockcode && <ItemBadge stockcode={altItem.stockcode} />}
												<Link href={`/${altItem.slug}`}>
													{Platform.OS === "web" ? (
														<p className="item-list-base-title-replacement">{altItem.name ? altItem.name : "No Item name detected"}</p>
													) : (
														<Typography.ParagraphSmall weight="bold">{altItem.name ? altItem.name : "No Item name detected"}</Typography.ParagraphSmall>
													)}
												</Link>
												<HStack gap={24}>
													{altItem.unitPrice && (
														<HStack marginTop={"auto"} gap={8}>
															<FormattedCurrency
																sx={{
																	flex: 1,
																	marginLeft: "auto",
																	fontWeight: "bold",
																	color: "$primary",
																}}
																amount={altItem.unitPrice}
															/>
														</HStack>
													)}
													<Box>
														<ItemStatusBadge item={altItem} location={null} />
													</Box>
												</HStack>
											</Box>
											<Button
												variant="outline"
												borderColor="transparent"
												padding={isMobile ? 0 : 12}
												onPress={() => selectReplacement(altItem)}
												disabled={replacingItems.has(altItem.stockcode)}
												sx={{
													opacity: replacingItems.has(altItem.stockcode) ? 0.6 : 1,
													cursor: replacingItems.has(altItem.stockcode) ? "not-allowed" : "pointer",
												}}
											>
												{replacingItems.has(altItem.stockcode) ? (
													<HStack alignItems="center" gap={8}>
														<ActivityIndicator size="small" color={resolvedPrimary} />
														<Typography.ParagraphSmall weight="bold" color="$primarylight">
															Replacing...
														</Typography.ParagraphSmall>
													</HStack>
												) : (
													<Typography.ParagraphSmall underline weight="bold" color="$primarylight">
														Select as replacement
													</Typography.ParagraphSmall>
												)}
											</Button>
										</HStack>
									</Box>
								))}
							</Box>
						) : (
							<Box p={16} bg="$basicwhite" borderRadius={6} borderWidth={1} borderColor="$neutral300">
								<Typography.ParagraphSmall color="$neutral600">No alternative products available for this item.</Typography.ParagraphSmall>
							</Box>
						)}
					</Box>
				</div>
			</div>
		);
	}

	// Fallback for mobile (React Native) - also need to always render

	return (
		<Box
			mt={12}
			bg="$neutral50"
			borderRadius={8}
			style={{
				height: isExpanded ? "auto" : 0,
				overflow: "hidden",
				padding: isExpanded ? 12 : 0,
			}}
		>
			<HStack alignItems="center" gap={8} mb={12}>
				<Typography weight="bold" color="$neutral800">
					Alternative Products
				</Typography>
			</HStack>

			{isLoading ? (
				<Box p={16} bg="$basicwhite" borderRadius={6} borderWidth={1} borderColor="$neutral300">
					<Typography.ParagraphSmall color="$neutral600">Loading alternative products...</Typography.ParagraphSmall>
				</Box>
			) : alternativeProducts?.list && alternativeProducts.list.length > 0 ? (
				<Box bg="$basicwhite" borderRadius={6} borderWidth={1} borderColor="$neutral300">
					{alternativeProducts.list?.map((altItem: any, index: number) => (
						<Box key={altItem.stockcode} p={12} borderBottomWidth={index < (alternativeProducts.list?.length || 0) - 1 ? 1 : 0} borderBottomColor="$neutral200">
							<HStack alignItems="center">
								{renderProductImage(altItem)}
								<Box flex={1}>
									{altItem.stockcode && <ItemBadge stockcode={altItem.stockcode} />}
									<Link href={`/${altItem.slug}`}>
										<Typography.ParagraphSmall weight="bold">{altItem.name ? altItem.name : "No Item name detected"}</Typography.ParagraphSmall>
									</Link>
									<HStack gap={24}>
										{altItem.unitPrice && (
											<HStack marginTop={"auto"} gap={8}>
												<FormattedCurrency
													sx={{
														flex: 1,
														marginLeft: "auto",
														fontWeight: "bold",
														color: "$primary",
													}}
													amount={altItem.unitPrice}
												/>
											</HStack>
										)}
										<Box>
											<ItemStatusBadge item={altItem} location={null} />
										</Box>
									</HStack>
								</Box>
								<Button
									variant="outline"
									borderColor="transparent"
									onPress={() => selectReplacement(altItem)}
									disabled={replacingItems.has(altItem.stockcode)}
									sx={{
										opacity: replacingItems.has(altItem.stockcode) ? 0.6 : 1,
									}}
								>
									{replacingItems.has(altItem.stockcode) ? (
										<HStack alignItems="center" gap={8}>
											<ActivityIndicator size="small" color={resolvedPrimary} />
											<Typography.ParagraphSmall weight="bold" color="$primarylight">
												Replacing...
											</Typography.ParagraphSmall>
										</HStack>
									) : (
										<Typography.ParagraphSmall underline weight="bold" color="$primarylight">
											Select as replacement
										</Typography.ParagraphSmall>
									)}
								</Button>
							</HStack>
						</Box>
					))}
				</Box>
			) : (
				<Box p={16} bg="$basicwhite" borderRadius={6} borderWidth={1} borderColor="$neutral300">
					<Typography.ParagraphSmall color="$neutral600">No alternative products available for this item.</Typography.ParagraphSmall>
				</Box>
			)}
		</Box>
	);
};

export default ItemListItemReplacementSection;
